import { createElement, useMemo } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { toast } from "sonner";
import { DistributionWidget } from "@/components/widget/DistributionWidget";
import { getDistributionWidgetConfig } from "@/lib/distributionWidgets";
import { SITE_URL } from "@/config/site";
import type { DistributionWidgetEmbedProps } from "@/types/distribution-widgets";

export const useDistributionWidget = ({ genie, kind }: DistributionWidgetEmbedProps) => {
  const configuredUrl = import.meta.env.VITE_APP_URL || window.location.origin;
  // A phone scanning a QR cannot reach this computer through "localhost".
  const baseUrl = ["localhost", "127.0.0.1", "[::1]"].includes(new URL(configuredUrl).hostname) ? SITE_URL : configuredUrl;
  const config = useMemo(() => getDistributionWidgetConfig(genie, kind, baseUrl), [genie, kind, baseUrl]);
  const embedCode = useMemo(() => config.href
    ? `<!-- Help Genie ${kind === "phone" ? "Phone" : "QR Code"} Widget -->\n${renderToStaticMarkup(createElement(DistributionWidget, { config }))}`
    : "", [config, kind]);

  const copyCode = async () => {
    if (!embedCode) return;
    try {
      await navigator.clipboard.writeText(embedCode);
      toast.success("Widget code copied!");
    } catch {
      toast.error("Couldn’t copy the code. Please try again.");
    }
  };

  const testWidget = () => {
    if (!embedCode) return;
    const tab = window.open("about:blank", "_blank");
    if (!tab) {
      toast.error("Allow pop-ups for this site, then try again.");
      return;
    }
    tab.opener = null;
    tab.document.open();
    tab.document.write(`<!doctype html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">
<title>Widget test | Help Genie</title></head>
<body style="margin:0;background:#F9FAFC;font-family:Poppins,system-ui,sans-serif;color:#222B45">
<main style="box-sizing:border-box;max-width:448px;margin:0 auto;padding:32px 16px">
<h1 style="margin:0 0 8px;font-size:24px;text-align:center">Try your ${kind === "phone" ? "phone" : "QR code"} widget</h1>
<p style="margin:0 0 24px;font-size:14px;line-height:1.6;text-align:center;color:#64748B">${kind === "phone" ? "Tap Call now to open your calling app, or dial the number shown." : "Scan with your phone camera, or choose Open on this device."}</p>
${embedCode}
</main></body></html>`);
    tab.document.close();
  };

  return { config, embedCode, copyCode, testWidget };
};
